import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  House, Plus, MagnifyingGlass, Clock, CheckCircle, Users, ArrowRight,
  Hammer, MapPin, Star, Phone, Envelope, ChatCircleDots, CaretDown, CaretUp,
  Briefcase, ShieldCheck, Warning, ArrowsClockwise, SignOut, Funnel,
  ClipboardText, PaperPlaneTilt, X, ArrowLeft, ChatCircle, Circle,
  Bell, BellRinging, BellSlash, CurrencyDollar, Gift, GearSix, Sparkle
} from '@phosphor-icons/react';
import { toast } from '../hooks/use-toast';
import { pushAPI } from '../api/api';
import { LOGO_URL } from '../config/branding';
import MembersFooter from '../components/MembersFooter';
import { ScheduleCallButton, ScheduledCallsList } from '../components/ScheduleCall';
import ReviewPromptCard from '../components/ReviewPromptCard';
import { PushNotifBanner } from '../components/homeowner/PushNotifBanner';
import { MessagingSidebar } from '../components/homeowner/MessagingComponents';
import { ContractorProfileModal } from '../components/homeowner/ContractorProfileModal';
import { ReviewModal } from '../components/homeowner/ReviewModal';
import AdminViewBanner from '../components/AdminViewBanner';
import BookingPromoBanner from '../components/BookingPromoBanner';
import DashboardThemeToggle from '../components/DashboardThemeToggle';
import OnboardingChecklist from '../components/OnboardingChecklist';
import PullToRefresh from '../components/PullToRefresh';
import { DashboardSkeleton } from '../components/Skeletons';
import { TelegramPromoCard } from '../components/TelegramPromoCard';
import { ChangePasswordCard } from '../components/ChangePasswordCard';
import { AccountPrivacyCard } from '../components/AccountPrivacyCard';
import PushNotificationSettings from '../components/PushNotificationSettings';
import TelegramLinkCard from '../components/TelegramLinkCard';
import { ClientEscrowsSection } from '../components/escrow/ClientEscrowsSection';
import { HomeownerTodayStrip } from '../components/homeowner/HomeownerTodayStrip';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  new: { label: 'Awaiting Contractors', color: 'bg-amber-500', textColor: 'text-amber-700', bgLight: 'bg-amber-50 dark:bg-amber-900/20' },
  contacted: { label: 'Contacted', color: 'bg-blue-500', textColor: 'text-blue-700', bgLight: 'bg-blue-50 dark:bg-blue-900/20' },
  converted: { label: 'Hired', color: 'bg-emerald-500', textColor: 'text-emerald-700', bgLight: 'bg-emerald-50 dark:bg-emerald-900/20' },
  closed: { label: 'Closed', color: 'bg-neutral-500', textColor: 'text-neutral-700', bgLight: 'bg-neutral-50 dark:bg-neutral-900/20' }
};


export default function HomeownerDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [leads, setLeads] = useState([]);
  const [expandedLead, setExpandedLead] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [user, setUser] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);

  // Messaging state
  const [messagingOpen, setMessagingOpen] = useState(false);
  const [activeChatPartner, setActiveChatPartner] = useState(null);

  // Account settings panel
  const [showSettings, setShowSettings] = useState(false);

  // Reviews state
  const [reviewableContractors, setReviewableContractors] = useState([]);
  const [reviewTarget, setReviewTarget] = useState(null);

  // Contractor profile preview state
  const [profileContractorId, setProfileContractorId] = useState(null);

  // Quote requests state
  const [quoteRequests, setQuoteRequests] = useState([]);
  const [acceptingQuote, setAcceptingQuote] = useState(null);

  // Site visit state
  const [confirmingVisit, setConfirmingVisit] = useState(null);

  // Active projects state
  const [activeProjects, setActiveProjects] = useState([]);

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login?type=homeowner');
      return;
    }
    const storedType = localStorage.getItem('userType');
    if (storedType && storedType !== 'homeowner' && storedType !== 'admin') {
      navigate(storedType === 'contractor' ? '/contractor-dashboard' : storedType === 'worker' ? '/worker-dashboard' : '/login');
      return;
    }
    if (storedUser) {
      try { setUser(JSON.parse(storedUser)); } catch { /* ignore */ }
    }
  }, [navigate]);

  const fetchDashboard = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/homeowner/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.status === 401) { navigate('/login?expired=true'); return; }
      if (res.status === 403) {
        toast({ title: 'Access Denied', description: 'Homeowner account required.', variant: 'destructive' });
        return;
      }
      const data = await res.json();
      if (data.success) setDashboard(data.dashboard);
    } catch (err) { console.error('Dashboard fetch error:', err); }
  }, [navigate]);

  const fetchLeads = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      const res = await fetch(`${API_URL}/api/homeowner/my-leads?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        if (data.success) setLeads(data.leads);
      }
    } catch (err) { console.error('Leads fetch error:', err); }
  }, [statusFilter]);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/messages/unread/count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        if (data.success) setUnreadCount(data.unread_count || 0);
      }
    } catch { /* ignore */ }
  }, []);

  const fetchReviewableContractors = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/homeowner/reviewable-contractors`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        if (data.success) setReviewableContractors(data.contractors || []);
      }
    } catch { /* ignore */ }
  }, []);

  const fetchQuoteRequests = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/homeowner/quote-requests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        if (data.success) setQuoteRequests(data.requests || []);
      }
    } catch { /* ignore */ }
  }, []);

  const fetchActiveProjects = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/project-tracker/active`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        if (data.success) setActiveProjects(data.projects || []);
      }
    } catch { /* ignore */ }
  }, []);

  const handleAcceptQuote = async (quoteRequestId, contractorName) => {
    setAcceptingQuote(quoteRequestId);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/homeowner/accept-quote`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ quote_request_id: quoteRequestId })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        toast({ title: 'Quote Accepted!', description: `${contractorName} has been notified. Great choice!` });
        await Promise.all([fetchQuoteRequests(), fetchLeads(), fetchDashboard(), fetchActiveProjects()]);
      } else {
        toast({ title: 'Error', description: data.detail || 'Failed to accept quote', variant: 'destructive' });
      }
    } catch {
      toast({ title: 'Error', description: 'Network error', variant: 'destructive' });
    } finally { setAcceptingQuote(null); }
  };

  const handleConfirmSiteVisit = async (quoteRequestId, slotIndex) => {
    setConfirmingVisit(quoteRequestId);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/contractor/quotes/requests/${quoteRequestId}/site-visit/confirm`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ selected_slot_index: slotIndex })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        toast({ title: 'Site Visit Confirmed!', description: `Visit scheduled for ${data.selected_slot?.date} at ${data.selected_slot?.time}` });
        await fetchQuoteRequests();
      } else {
        toast({ title: 'Error', description: data.detail || 'Failed to confirm', variant: 'destructive' });
      }
    } catch {
      toast({ title: 'Error', description: 'Network error', variant: 'destructive' });
    } finally { setConfirmingVisit(null); }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchDashboard(), fetchLeads(), fetchUnreadCount(), fetchReviewableContractors(), fetchQuoteRequests(), fetchActiveProjects()]);
      setLoading(false);
    };
    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional mount-only effect
  }, [fetchDashboard, fetchLeads, fetchUnreadCount]);

  // Poll unread count
  useEffect(() => {
    const interval = setInterval(fetchUnreadCount, 10000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('userType');
    localStorage.removeItem('userId');
    localStorage.removeItem('userName');
    window.location.href = '/';
  };

  const openChatWith = (contractorId, contractorName, contractorImage) => {
    setActiveChatPartner({ id: contractorId, name: contractorName, image: contractorImage });
    setMessagingOpen(true);
  };

  const openInbox = () => {
    setActiveChatPartner(null);
    setMessagingOpen(true);
  };

  const currentUserId = user?._id || localStorage.getItem('userId');

  if (loading) {
    return <DashboardSkeleton />;
  }

  return (
    <PullToRefresh onRefresh={() => Promise.all([fetchDashboard(), fetchLeads(), fetchUnreadCount(), fetchQuoteRequests(), fetchActiveProjects()])}>
    <div className="ios-dash min-h-screen bg-[#F4F7FC] dark:bg-[#121212]" data-testid="homeowner-dashboard">
      <AdminViewBanner role="homeowner" />
      {/* Top Bar */}
      <div className="bg-[#2563EB] border-b-4 border-[#0F172A]">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" className="flex items-center gap-2">
              <img src={LOGO_URL} alt="ConstructHelp" className="h-10 w-auto" />
            </Link>
          </div>
          <div className="flex items-center gap-3">
            {/* Refer & Earn Button */}
            <button
              onClick={() => navigate('/referrals')}
              className="hidden sm:flex items-center gap-2 px-4 py-2 bg-[#3B82F6] hover:bg-[#1E40AF] text-[#0F172A] font-bold text-sm border border-[#0F172A] transition-colors"
              data-testid="refer-earn-btn"
            >
              <Gift size={18} weight="bold" />
              <span className="hidden md:inline">Refer & Earn</span>
            </button>
            {/* Messages Button */}
            <button
              onClick={openInbox}
              className="relative flex items-center gap-2 px-4 py-2 bg-[#2563EB] hover:bg-[#1D4ED8] text-[#0F172A] font-bold text-sm border border-[#203A55] transition-colors"
              data-testid="inbox-btn"
            >
              <ChatCircleDots size={18} weight="bold" />
              <span className="hidden sm:inline">Messages</span>
              {unreadCount > 0 && (
                <span className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-[#3B82F6] text-[#0F172A] text-[10px] font-bold flex items-center justify-center border border-[#0F172A]">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>
            <span className="text-[#0F172A] font-medium text-sm hidden md:block">
              {user?.name || user?.email}
            </span>
            <button
              onClick={() => setShowSettings(true)}
              className="flex items-center gap-2 px-4 py-2 bg-white hover:bg-[#F0EDE6] text-[#0F172A] font-bold text-sm border border-[#0F172A] transition-colors"
              data-testid="account-settings-btn"
            >
              <GearSix size={18} weight="bold" />
              <span className="hidden sm:inline">Settings</span>
            </button>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-4 py-2 bg-white hover:bg-[#F0EDE6] text-[#0F172A] font-bold text-sm border border-[#0F172A] transition-colors"
              data-testid="logout-btn"
            >
              <SignOut size={18} weight="bold" />
              <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        <DashboardThemeToggle />
        <TelegramPromoCard />
        <BookingPromoBanner role="homeowner" />
        <OnboardingChecklist role="homeowner" />
        {/* Welcome Header */}
        <div className="mb-8">
          <h1 className="text-2xl md:text-3xl font-black uppercase tracking-tight text-neutral-900 dark:text-white">
            My Projects
          </h1>
          <p className="text-neutral-600 dark:text-neutral-400 mt-1">
            Track your home improvement requests and hired contractors
          </p>
        </div>

        {/* Push Notification Banner */}
        <PushNotifBanner />

        {/* Action Bar */}
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Funnel size={20} weight="bold" className="text-neutral-500" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="h-10 px-3 bg-white dark:bg-[#0F172A] border-2 border-[#0F172A] font-bold text-sm"
              data-testid="status-filter"
            >
              <option value="">All Requests</option>
              <option value="new">Awaiting Contractors</option>
              <option value="contacted">Contacted</option>
              <option value="converted">Hired</option>
              <option value="closed">Closed</option>
            </select>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <button
              onClick={() => navigate('/ai-project')}
              className="h-12 px-5 bg-violet-600 hover:bg-violet-700 text-white font-bold uppercase tracking-wider border-2 border-[#0F172A] shadow-[4px_4px_0px_#0F172A] hover:shadow-[5px_5px_0px_#0F172A] transition-all flex items-center gap-2"
              data-testid="ai-project-btn"
            >
              <Sparkle size={20} weight="fill" />
              Describe It To AI
            </button>
            <button
              onClick={() => navigate('/get-quotes')}
              className="h-12 px-6 bg-[#2563EB] hover:bg-[#1D4ED8] text-[#0F172A] font-bold uppercase tracking-wider border-2 border-[#0F172A] shadow-[4px_4px_0px_#0F172A] hover:shadow-[5px_5px_0px_#0F172A] transition-all flex items-center gap-2"
              data-testid="new-request-btn"
            >
              <Plus size={20} weight="bold" />
              New Project Request
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        {dashboard && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white dark:bg-[#0F172A] border-3 border-[#0F172A] shadow-[4px_4px_0px_#2563EB] p-4" data-testid="stat-total-requests">
              <div className="flex items-center gap-2 mb-2">
                <ClipboardText size={20} weight="bold" className="text-[#64748B]" />
                <span className="text-xs font-bold uppercase text-neutral-500">Requests</span>
              </div>
              <p className="text-3xl font-black text-neutral-900 dark:text-white">{dashboard.total_leads}</p>
            </div>
            <div className="bg-white dark:bg-[#0F172A] border-3 border-[#0F172A] shadow-[4px_4px_0px_#3B82F6] p-4" data-testid="stat-awaiting">
              <div className="flex items-center gap-2 mb-2">
                <Clock size={20} weight="bold" className="text-[#475569]" />
                <span className="text-xs font-bold uppercase text-neutral-500">Awaiting</span>
              </div>
              <p className="text-3xl font-black text-neutral-900 dark:text-white">{dashboard.new_leads}</p>
            </div>
            <div className="bg-white dark:bg-[#0F172A] border-3 border-[#0F172A] shadow-[4px_4px_0px_#D1FAE5] p-4" data-testid="stat-contractors">
              <div className="flex items-center gap-2 mb-2">
                <Users size={20} weight="bold" className="text-emerald-500" />
                <span className="text-xs font-bold uppercase text-neutral-500">Contractors</span>
              </div>
              <p className="text-3xl font-black text-neutral-900 dark:text-white">{dashboard.contractors_interested}</p>
            </div>
            <div className="bg-white dark:bg-[#0F172A] border-3 border-[#0F172A] shadow-[4px_4px_0px_#EFF6FF] p-4" data-testid="stat-active-jobs">
              <div className="flex items-center gap-2 mb-2">
                <Hammer size={20} weight="bold" className="text-purple-500" />
                <span className="text-xs font-bold uppercase text-neutral-500">Active Jobs</span>
              </div>
              <p className="text-3xl font-black text-neutral-900 dark:text-white">{dashboard.active_jobs}</p>
            </div>
          </div>
        )}

        {/* Quotes Received - Comparison Section */}
        <HomeownerTodayStrip
          quotesWaiting={quoteRequests.filter((q) => q.status === 'quoted').length}
          unreadCount={unreadCount}
          activeProjects={activeProjects.length}
        />
        {(() => {
          const quoted = quoteRequests.filter(qr => ['quoted', 'accepted', 'not_selected', 'site_visit_requested', 'site_visit_scheduled', 'site_visit_completed'].includes(qr.status));
          if (quoted.length === 0) return null;

          // Group by lead_id
          const byLead = {};
          quoted.forEach(qr => {
            if (!byLead[qr.lead_id]) byLead[qr.lead_id] = { job: qr.job_type_display, location: qr.location, quotes: [] };
            byLead[qr.lead_id].quotes.push(qr);
          });
          // Sort quotes within each group: lowest price first
          Object.values(byLead).forEach(g => g.quotes.sort((a, b) => (a.price_estimate || Infinity) - (b.price_estimate || Infinity)));

          return (
            <div className="mt-8" data-testid="quotes-received-section">
              <h2 className="text-lg font-black uppercase tracking-tight text-neutral-900 dark:text-white mb-4 flex items-center gap-2">
                <CurrencyDollar size={22} weight="bold" className="text-emerald-500" />
                Quotes Received
                <span className="text-sm font-bold text-neutral-400 normal-case">— compare & accept</span>
              </h2>

              <div className="space-y-6">
                {Object.entries(byLead).map(([leadId, group]) => {
                  const hasAccepted = group.quotes.some(q => q.status === 'accepted');
                  const prices = group.quotes.filter(q => q.price_estimate).map(q => q.price_estimate);
                  const lowest = prices.length ? Math.min(...prices) : null;

                  return (
                    <div key={leadId} className="bg-white dark:bg-[#0F172A] border-3 border-[#0F172A] shadow-[4px_4px_0px_#0F172A]" data-testid={`quote-group-${leadId}`}>
                      {/* Project Header */}
                      <div className="p-4 bg-neutral-50 dark:bg-neutral-900/50 border-b-2 border-[#0F172A] flex items-center justify-between">
                        <div>
                          <h3 className="font-black text-neutral-900 dark:text-white uppercase text-sm">{group.job}</h3>
                          <p className="text-xs text-neutral-500 flex items-center gap-1 mt-0.5">
                            <MapPin size={12} weight="bold" />{group.location} — {group.quotes.length} quote{group.quotes.length !== 1 ? 's' : ''}
                          </p>
                        </div>
                        {hasAccepted && (
                          <span className="px-3 py-1 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 text-xs font-bold border border-emerald-300 dark:border-emerald-700 flex items-center gap-1">
                            <CheckCircle size={14} weight="bold" /> Contractor Selected
                          </span>
                        )}
                      </div>

                      {/* Quotes Grid */}
                      <div className={`grid gap-0 divide-x divide-neutral-200 dark:divide-neutral-700 ${group.quotes.length === 1 ? 'grid-cols-1' : group.quotes.length === 2 ? 'grid-cols-2' : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'}`}>
                        {group.quotes.map((q) => {
                          const isLowest = lowest !== null && q.price_estimate === lowest && prices.length > 1;
                          const isAccepted = q.status === 'accepted';
                          const isNotSelected = q.status === 'not_selected';

                          return (
                            <div
                              key={q.id}
                              className={`p-4 relative ${isAccepted ? 'bg-emerald-50 dark:bg-emerald-900/10' : isNotSelected ? 'opacity-60' : ''}`}
                              data-testid={`quote-card-${q.id}`}
                            >
                              {/* Best Price Tag */}
                              {isLowest && !hasAccepted && (
                                <div className="absolute top-2 right-2 px-2 py-0.5 bg-[#3B82F6] text-[#0F172A] text-[10px] font-bold uppercase" data-testid={`best-price-${q.id}`}>
                                  Best Price
                                </div>
                              )}

                              {/* Accepted Badge */}
                              {isAccepted && (
                                <div className="absolute top-2 right-2 px-2 py-0.5 bg-[#D1FAE5] text-[#0F172A] text-[10px] font-bold uppercase flex items-center gap-1">
                                  <CheckCircle size={12} weight="bold" /> Accepted
                                </div>
                              )}

                              {/* Contractor Info */}
                              <div className="flex items-center gap-2 mb-3 cursor-pointer" onClick={() => setProfileContractorId(q.contractor_id)}>
                                <div className="w-9 h-9 bg-[#2563EB] border-2 border-[#0F172A] flex items-center justify-center overflow-hidden flex-shrink-0">
                                  {q.contractor_image ? (
                                    <img src={q.contractor_image} alt="" className="w-full h-full object-cover" />
                                  ) : (
                                    <Briefcase size={16} weight="bold" className="text-white" />
                                  )}
                                </div>
                                <div className="min-w-0">
                                  <p className="font-bold text-neutral-900 dark:text-white text-sm truncate hover:text-[#64748B] transition-colors">{q.contractor_name}</p>
                                  {q.contractor_company && <p className="text-[10px] text-neutral-500 truncate">{q.contractor_company}</p>}
                                </div>
                              </div>

                              {/* Price */}
                              <div className="mb-2">
                                <p className={`text-2xl font-black ${isAccepted ? 'text-emerald-600' : 'text-neutral-900 dark:text-white'}`}>
                                  ${q.price_estimate ? Number(q.price_estimate).toLocaleString() : '—'}
                                </p>
                                {q.response_timeline && (
                                  <p className="text-xs text-neutral-500 flex items-center gap-1 mt-0.5">
                                    <Clock size={12} weight="bold" /> {q.response_timeline}
                                  </p>
                                )}
                              </div>

                              {/* Rating */}
                              {q.contractor_rating > 0 && (
                                <div className="flex items-center gap-1 mb-2">
                                  <Star size={14} weight="fill" className="text-amber-400" />
                                  <span className="text-xs font-bold text-neutral-600 dark:text-neutral-400">{Number(q.contractor_rating).toFixed(1)}</span>
                                  {q.contractor_reviews > 0 && <span className="text-xs text-neutral-400">({q.contractor_reviews})</span>}
                                </div>
                              )}

                              {/* Message */}
                              {q.response_message && (
                                <p className="text-xs text-neutral-600 dark:text-neutral-400 leading-relaxed mb-2 line-clamp-3">
                                  "{q.response_message}"
                                </p>
                              )}

                              {/* Site Visit Photos (shown on quoted cards that had a site visit) */}
                              {q.site_visit?.photos?.length > 0 && q.status === 'quoted' && (
                                <div className="mb-3" data-testid={`quoted-visit-gallery-${q.id}`}>
                                  <p className="text-[10px] font-bold text-neutral-500 uppercase mb-1">Site Visit Photos</p>
                                  <div className="flex flex-wrap gap-1">
                                    {q.site_visit.photos.slice(0, 4).map((photo, idx) => (
                                      <img key={idx} src={photo} alt="" className="h-12 w-12 object-cover border border-neutral-300 dark:border-neutral-600" />
                                    ))}
                                    {q.site_visit.photos.length > 4 && (
                                      <div className="h-12 w-12 bg-neutral-200 dark:bg-neutral-700 border border-neutral-300 dark:border-neutral-600 flex items-center justify-center text-xs font-bold text-neutral-500">
                                        +{q.site_visit.photos.length - 4}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}

                              {/* Actions */}
                              {!hasAccepted && q.status === 'quoted' && (
                                <div className="flex gap-2 mt-auto">
                                  <button
                                    onClick={() => handleAcceptQuote(q.id, q.contractor_name)}
                                    disabled={acceptingQuote === q.id}
                                    className={`flex-1 h-9 text-xs font-bold uppercase tracking-wider border-2 border-[#0F172A] shadow-[2px_2px_0px_#0F172A] hover:shadow-[3px_3px_0px_#0F172A] transition-all flex items-center justify-center gap-1 ${
                                      isLowest
                                        ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                                        : 'bg-[#2563EB] hover:bg-[#1D4ED8] text-[#0F172A]'
                                    } disabled:opacity-40`}
                                    data-testid={`accept-quote-${q.id}`}
                                  >
                                    {acceptingQuote === q.id ? (
                                      <ArrowsClockwise size={14} weight="bold" className="animate-spin" />
                                    ) : (
                                      <CheckCircle size={14} weight="bold" />
                                    )}
                                    Accept
                                  </button>
                                  <button
                                    onClick={(e) => { e.stopPropagation(); openChatWith(q.contractor_id, q.contractor_name, q.contractor_image); }}
                                    className="h-9 w-9 flex items-center justify-center bg-neutral-100 dark:bg-neutral-800 hover:bg-neutral-200 dark:hover:bg-neutral-700 border-2 border-[#0F172A] text-neutral-600 dark:text-neutral-400 transition-colors"
                                    data-testid={`chat-quote-${q.id}`}
                                  >
                                    <ChatCircleDots size={16} weight="bold" />
                                  </button>
                                </div>
                              )}

                              {/* Site Visit: Pick a time slot */}
                              {q.status === 'site_visit_requested' && q.site_visit?.proposed_slots && (
                                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 p-3 mt-2" data-testid={`site-visit-pick-${q.id}`}>
                                  <p className="text-xs font-bold text-blue-700 dark:text-blue-400 uppercase mb-2 flex items-center gap-1">
                                    <MapPin size={14} weight="bold" /> Site Visit — Pick a Time
                                  </p>
                                  {q.site_visit.message && <p className="text-xs text-neutral-600 dark:text-neutral-400 mb-2 italic">"{q.site_visit.message}"</p>}
                                  <div className="space-y-1.5">
                                    {q.site_visit.proposed_slots.map((slot, idx) => (
                                      <button
                                        key={idx}
                                        onClick={() => handleConfirmSiteVisit(q.id, idx)}
                                        disabled={confirmingVisit === q.id}
                                        className="w-full h-9 px-3 bg-white dark:bg-neutral-800 hover:bg-blue-100 dark:hover:bg-blue-900/30 text-sm font-bold text-neutral-900 dark:text-white border-2 border-blue-300 dark:border-blue-700 flex items-center justify-between gap-2 transition-colors disabled:opacity-40"
                                        data-testid={`pick-slot-${q.id}-${idx}`}
                                      >
                                        <span className="flex items-center gap-2">
                                          <Clock size={14} weight="bold" className="text-blue-500" />
                                          {slot.date} at {slot.time}
                                        </span>
                                        <span className="text-xs text-blue-600">Select</span>
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* Site Visit: Scheduled */}
                              {q.status === 'site_visit_scheduled' && (
                                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 p-3 mt-2">
                                  <p className="text-xs font-bold text-blue-700 dark:text-blue-400 uppercase mb-1 flex items-center gap-1">
                                    <MapPin size={14} weight="bold" /> Site Visit Scheduled
                                  </p>
                                  <p className="text-sm font-bold text-neutral-900 dark:text-white">
                                    {q.site_visit?.selected_slot?.date} at {q.site_visit?.selected_slot?.time}
                                  </p>
                                  <p className="text-xs text-neutral-500 mt-1">The contractor will visit your property and send a final quote afterward.</p>
                                </div>
                              )}

                              {/* Site Visit: Completed — show notes */}
                              {q.status === 'site_visit_completed' && (
                                <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 p-3 mt-2">
                                  <p className="text-xs font-bold text-emerald-700 dark:text-emerald-400 uppercase mb-1 flex items-center gap-1">
                                    <CheckCircle size={14} weight="bold" /> Site Visit Complete
                                  </p>
                                  <p className="text-sm text-neutral-700 dark:text-neutral-300">{q.site_visit?.notes}</p>
                                  {q.site_visit?.estimated_price && (
                                    <p className="text-sm font-bold text-emerald-600 mt-1">Preliminary estimate: ${Number(q.site_visit.estimated_price).toLocaleString()}</p>
                                  )}
                                  {q.site_visit?.photos?.length > 0 && (
                                    <div className="mt-2" data-testid={`visit-gallery-homeowner-${q.id}`}>
                                      <p className="text-[10px] font-bold text-emerald-600 uppercase mb-1">Site Photos ({q.site_visit.photos.length})</p>
                                      <div className="flex flex-wrap gap-1.5">
                                        {q.site_visit.photos.map((photo, idx) => (
                                          <img key={idx} src={photo} alt={`Site photo ${idx + 1}`} className="h-20 w-20 object-cover border-2 border-emerald-300 dark:border-emerald-700 cursor-pointer hover:opacity-80 transition-opacity" data-testid={`homeowner-visit-photo-${q.id}-${idx}`} />
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                  <p className="text-xs text-neutral-500 mt-2">Waiting for contractor's final quote...</p>
                                </div>
                              )}

                              {isAccepted && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); openChatWith(q.contractor_id, q.contractor_name, q.contractor_image); }}
                                  className="w-full h-9 text-xs font-bold uppercase tracking-wider bg-emerald-600 hover:bg-emerald-700 text-white border-2 border-[#0F172A] shadow-[2px_2px_0px_#0F172A] transition-all flex items-center justify-center gap-1 mt-auto"
                                  data-testid={`message-accepted-${q.id}`}
                                >
                                  <ChatCircleDots size={14} weight="bold" /> Message Contractor
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })()}

        {/* Payment Protection (Client Escrow) */}
        <ClientEscrowsSection role="homeowner" />

        {/* Active Projects */}
        {activeProjects.length > 0 && (
          <div className="mt-8" data-testid="active-projects-section">
            <h2 className="text-lg font-black uppercase tracking-tight text-neutral-900 dark:text-white mb-4 flex items-center gap-2">
              <Hammer size={22} weight="bold" className="text-[#64748B]" />
              Active Projects
            </h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {activeProjects.map((p) => {
                const stCfg = { planning: 'bg-blue-500', in_progress: 'bg-amber-500', completed: 'bg-emerald-500', on_hold: 'bg-neutral-500' };
                const stLabel = { planning: 'Planning', in_progress: 'In Progress', completed: 'Completed', on_hold: 'On Hold' };
                return (
                  <div
                    key={p.id}
                    onClick={() => navigate(`/project/${p.id}`)}
                    className="bg-white dark:bg-[#0F172A] border-3 border-[#0F172A] shadow-[4px_4px_0px_#2563EB] hover:shadow-[6px_6px_0px_#2563EB] p-4 cursor-pointer transition-all hover:-translate-y-0.5"
                    data-testid={`project-card-${p.id}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-black text-neutral-900 dark:text-white uppercase text-sm truncate flex-1">{p.title}</h3>
                      <span className={`px-2 py-0.5 ${stCfg[p.status] || 'bg-neutral-500'} text-white text-[10px] font-bold ml-2`}>
                        {stLabel[p.status] || p.status}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-neutral-500 mb-3">
                      <span className="flex items-center gap-1"><Briefcase size={12} weight="bold" /> {p.contractor_name}</span>
                      {p.budget > 0 && <span className="text-emerald-600 font-bold">${Number(p.budget).toLocaleString()}</span>}
                    </div>
                    {/* Escrow status */}
                    {p.escrow_status === 'funded' && (
                      <div className="flex items-center gap-2 text-[10px] font-bold mb-2">
                        <span className="px-1.5 py-0.5 bg-emerald-100 text-emerald-700 border border-emerald-300">Escrow Funded</span>
                        <span className="text-emerald-600">${Number(p.escrow_released || 0).toLocaleString()} released</span>
                        <span className="text-neutral-400">/ ${Number(p.escrow_total || 0).toLocaleString()}</span>
                      </div>
                    )}
                    <div className="w-full h-2.5 bg-neutral-200 dark:bg-neutral-700 border border-[#0F172A] overflow-hidden mb-1">
                      <div className="h-full bg-emerald-500 transition-all" style={{ width: `${p.progress}%` }} />
                    </div>
                    <div className="flex items-center justify-between text-[10px] text-neutral-500">
                      <span>{p.progress}% complete</span>
                      <span>{p.completed_milestones}/{p.total_milestones} milestones</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Leads List */}
        {leads.length === 0 ? (
          <div className="bg-white dark:bg-[#0F172A] border-4 border-[#0F172A] shadow-[6px_6px_0px_#2563EB] p-12 text-center">
            <House size={64} weight="bold" className="text-neutral-300 dark:text-neutral-600 mx-auto mb-4" />
            <h3 className="text-xl font-black text-neutral-900 dark:text-white mb-2 uppercase">
              No Project Requests Yet
            </h3>
            <p className="text-neutral-600 dark:text-neutral-400 mb-6 max-w-md mx-auto">
              Submit your first project request to get matched with verified local contractors.
            </p>
            <button
              onClick={() => navigate('/get-quotes')}
              className="h-14 px-8 bg-[#2563EB] hover:bg-[#1D4ED8] text-[#0F172A] font-bold uppercase tracking-wider border-2 border-[#0F172A] shadow-[4px_4px_0px_#0F172A] hover:shadow-[6px_6px_0px_#0F172A] transition-all inline-flex items-center gap-2"
              data-testid="empty-state-cta"
            >
              Get Free Quotes
              <ArrowRight size={22} weight="bold" />
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {leads.map((lead) => {
              const statusCfg = STATUS_CONFIG[lead.status] || STATUS_CONFIG.new;
              const isExpanded = expandedLead === lead.id;

              return (
                <div
                  key={lead.id}
                  className="bg-white dark:bg-[#0F172A] border-3 border-[#0F172A] shadow-[4px_4px_0px_#0F172A] overflow-hidden"
                  data-testid={`lead-card-${lead.id}`}
                >
                  {/* Lead Header */}
                  <button
                    onClick={() => setExpandedLead(isExpanded ? null : lead.id)}
                    className="w-full p-4 sm:p-5 flex items-center justify-between hover:bg-neutral-50 dark:hover:bg-neutral-800/50 transition-colors text-left"
                    data-testid={`lead-toggle-${lead.id}`}
                  >
                    <div className="flex items-center gap-4 flex-1 min-w-0">
                      <div className={`w-12 h-12 ${statusCfg.color} border-2 border-[#0F172A] flex items-center justify-center flex-shrink-0`}>
                        <Hammer size={24} weight="bold" className="text-white" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="font-black text-neutral-900 dark:text-white uppercase text-sm sm:text-base truncate">
                          {lead.job_type_display}
                        </h3>
                        <div className="flex items-center gap-3 mt-1 text-xs text-neutral-500">
                          <span className="flex items-center gap-1">
                            <MapPin size={14} weight="bold" />
                            {lead.location?.city || lead.location?.zip_code}{lead.location?.state ? `, ${lead.location.state}` : ''}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock size={14} weight="bold" />
                            {new Date(lead.created_at).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 flex-shrink-0 ml-2">
                      {lead.contractors_interested > 0 && (
                        <span className="hidden sm:flex items-center gap-1 px-3 py-1 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 text-xs font-bold border border-emerald-300 dark:border-emerald-700">
                          <Users size={14} weight="bold" />
                          {lead.contractors_interested} interested
                        </span>
                      )}
                      <span className={`px-3 py-1 ${statusCfg.bgLight} ${statusCfg.textColor} text-xs font-bold border`}>
                        {statusCfg.label}
                      </span>
                      {isExpanded ? (
                        <CaretUp size={20} weight="bold" className="text-neutral-400" />
                      ) : (
                        <CaretDown size={20} weight="bold" className="text-neutral-400" />
                      )}
                    </div>
                  </button>

                  {/* Expanded Content */}
                  {isExpanded && (
                    <div className="border-t-2 border-[#0F172A]">
                      {/* Project Details */}
                      <div className="p-4 sm:p-5 bg-neutral-50 dark:bg-neutral-900/50">
                        <div className="grid sm:grid-cols-2 gap-4 mb-4">
                          <div>
                            <p className="text-xs font-bold uppercase text-neutral-500 mb-1">Timeline</p>
                            <p className="font-bold text-neutral-900 dark:text-white text-sm">{lead.timeline_display}</p>
                          </div>
                          <div>
                            <p className="text-xs font-bold uppercase text-neutral-500 mb-1">Status</p>
                            <p className={`font-bold text-sm ${statusCfg.textColor}`}>{statusCfg.label}</p>
                          </div>
                        </div>
                        <div>
                          <p className="text-xs font-bold uppercase text-neutral-500 mb-1">Project Description</p>
                          <p className="text-sm text-neutral-700 dark:text-neutral-300 leading-relaxed">{lead.description}</p>
                        </div>
                      </div>

                      {/* Interested Contractors */}
                      {lead.contractors && lead.contractors.length > 0 && (
                        <div className="p-4 sm:p-5 border-t-2 border-dashed border-neutral-300 dark:border-neutral-700">
                          <h4 className="text-sm font-black uppercase text-neutral-900 dark:text-white mb-3 flex items-center gap-2">
                            <Users size={18} weight="bold" className="text-[#64748B]" />
                            Interested Contractors ({lead.contractors.length})
                          </h4>
                          <div className="space-y-3">
                            {lead.contractors.map((contractor, idx) => (
                              <div
                                key={idx}
                                className="flex items-center justify-between p-3 bg-white dark:bg-[#0F172A] border-2 border-[#0F172A]"
                              >
                                <div className="flex items-center gap-3 cursor-pointer group/name" onClick={(e) => { e.stopPropagation(); setProfileContractorId(contractor.contractor_id); }} data-testid={`contractor-name-${contractor.contractor_id}`}>
                                  <div className="w-10 h-10 bg-[#2563EB] border-2 border-[#0F172A] flex items-center justify-center overflow-hidden flex-shrink-0 group-hover/name:border-[#3B82F6] transition-colors">
                                    {contractor.profile_image ? (
                                      <img src={contractor.profile_image} alt="" className="w-full h-full object-cover" />
                                    ) : (
                                      <Briefcase size={20} weight="bold" className="text-white" />
                                    )}
                                  </div>
                                  <div>
                                    <p className="font-bold text-neutral-900 dark:text-white text-sm group-hover/name:text-[#64748B] transition-colors underline decoration-transparent group-hover/name:decoration-[#64748B]">{contractor.name}</p>
                                    {contractor.company && (
                                      <p className="text-xs text-neutral-500">{contractor.company}</p>
                                    )}
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  {quoteRequests.some(qr => qr.contractor_id === contractor.contractor_id && qr.lead_id === lead.id) && (
                                    <span className="hidden sm:flex items-center gap-1 px-2 py-1 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 text-[10px] font-bold border border-emerald-300 dark:border-emerald-700" data-testid={`quote-sent-badge-${contractor.contractor_id}`}>
                                      <CheckCircle size={12} weight="bold" /> Quote Sent
                                    </span>
                                  )}
                                  {contractor.rating > 0 && (
                                    <span className="flex items-center gap-1 text-xs font-bold text-amber-600">
                                      <Star size={14} weight="fill" />
                                      {contractor.rating.toFixed(1)}
                                    </span>
                                  )}
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openChatWith(contractor.contractor_id, contractor.name, contractor.profile_image);
                                    }}
                                    className="px-3 py-1.5 bg-[#2563EB] hover:bg-[#1D4ED8] text-[#0F172A] text-xs font-bold border-2 border-[#0F172A] shadow-[2px_2px_0px_#0F172A] transition-all flex items-center gap-1"
                                    data-testid={`message-contractor-${contractor.contractor_id}`}
                                  >
                                    <ChatCircleDots size={16} weight="bold" />
                                    <span className="hidden sm:inline">Message</span>
                                  </button>
                                  <ScheduleCallButton partnerId={contractor.contractor_id} partnerName={contractor.name} className="px-3 py-1.5 bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300 text-xs font-bold border-2 border-violet-300 dark:border-violet-700 hover:bg-violet-200 transition-all" />
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* No contractors yet */}
                      {(!lead.contractors || lead.contractors.length === 0) && (
                        <div className="p-4 sm:p-5 border-t-2 border-dashed border-neutral-300 dark:border-neutral-700 text-center">
                          <MagnifyingGlass size={32} weight="bold" className="text-neutral-300 dark:text-neutral-600 mx-auto mb-2" />
                          <p className="text-sm text-neutral-500 font-bold">
                            We're matching you with contractors in your area.
                          </p>
                          <p className="text-xs text-neutral-400 mt-1">
                            Interested contractors will appear here once they view your request.
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Scheduled Calls */}
        <div className="mt-8">
          <ScheduledCallsList />
        </div>

        {/* Review Prompts — leave a review for completed projects */}
        <div className="mt-6">
          <ReviewPromptCard />
        </div>

        {/* Contractor Reviews Section */}
        {reviewableContractors.length > 0 && (
          <div className="mt-8" data-testid="reviews-section">
            <h2 className="text-lg font-black uppercase tracking-tight text-neutral-900 dark:text-white mb-4 flex items-center gap-2">
              <Star size={22} weight="bold" className="text-[#475569]" />
              Rate Your Contractors
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {reviewableContractors.map((c) => (
                <div
                  key={c.contractor_id}
                  className="bg-white dark:bg-[#0F172A] border-3 border-[#0F172A] shadow-[4px_4px_0px_#0F172A] p-4"
                  data-testid={`review-card-${c.contractor_id}`}
                >
                  <div className="flex items-center gap-3 mb-3 cursor-pointer group/rname" onClick={() => setProfileContractorId(c.contractor_id)}>
                    <div className="w-10 h-10 bg-[#2563EB] border-2 border-[#0F172A] flex items-center justify-center overflow-hidden flex-shrink-0 group-hover/rname:border-[#3B82F6] transition-colors">
                      {c.profile_image ? (
                        <img src={c.profile_image} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <Briefcase size={18} weight="bold" className="text-white" />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-bold text-neutral-900 dark:text-white text-sm truncate group-hover/rname:text-[#64748B] transition-colors">{c.name}</p>
                      {c.company && <p className="text-xs text-neutral-500 truncate">{c.company}</p>}
                    </div>
                  </div>

                  {c.already_reviewed && c.my_review ? (
                    <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 p-3">
                      <div className="flex items-center gap-1 mb-1">
                        {[1,2,3,4,5].map((s) => (
                          <Star key={s} size={14} weight={s <= c.my_review.rating ? 'fill' : 'regular'}
                            className={s <= c.my_review.rating ? 'text-amber-400' : 'text-neutral-300'} />
                        ))}
                      </div>
                      <p className="text-xs font-bold text-neutral-800 dark:text-neutral-200">{c.my_review.title}</p>
                      <p className="text-xs text-neutral-600 dark:text-neutral-400 mt-1 line-clamp-2">{c.my_review.content}</p>
                      <p className="text-[10px] text-neutral-400 mt-1 flex items-center gap-1">
                        <CheckCircle size={12} weight="bold" className="text-emerald-500" /> Reviewed
                      </p>
                    </div>
                  ) : (
                    <button
                      onClick={() => setReviewTarget(c)}
                      className="w-full h-10 bg-[#3B82F6] hover:bg-[#E55D00] text-white text-xs font-bold uppercase tracking-wider border-2 border-[#0F172A] shadow-[2px_2px_0px_#0F172A] hover:shadow-[3px_3px_0px_#0F172A] transition-all flex items-center justify-center gap-2"
                      data-testid={`write-review-${c.contractor_id}`}
                    >
                      <Star size={16} weight="bold" />
                      Write a Review
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Help Text */}
        <div className="mt-8 bg-white dark:bg-[#0F172A] border-3 border-[#0F172A] shadow-[4px_4px_0px_#2563EB] p-5">
          <div className="flex items-start gap-3">
            <ShieldCheck size={24} weight="bold" className="text-[#64748B] flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-black text-neutral-900 dark:text-white text-sm uppercase mb-1">How it works</h4>
              <p className="text-sm text-neutral-600 dark:text-neutral-400 leading-relaxed">
                After you submit a project request, verified contractors in your area review it and express interest.
                You'll see them listed under each request. You can message them directly, compare their profiles, and
                choose the best fit for your project. There's no obligation to hire.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Account Settings Panel */}
      {showSettings && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center overflow-y-auto py-10 px-4" onClick={() => setShowSettings(false)} data-testid="account-settings-panel">
          <div className="max-w-xl w-full space-y-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between bg-[#0F172A] border-4 border-[#0F172A] p-4 shadow-[6px_6px_0px_#2563EB]">
              <h2 className="text-lg font-black uppercase text-white flex items-center gap-2">
                <GearSix size={22} weight="bold" className="text-[#64748B]" /> Account Settings
              </h2>
              <button
                onClick={() => setShowSettings(false)}
                className="w-9 h-9 bg-white text-black border-2 border-[#0F172A] flex items-center justify-center hover:bg-neutral-200 transition-colors"
                data-testid="close-settings-btn"
              >
                <X size={18} weight="bold" />
              </button>
            </div>
            <ChangePasswordCard />
            <AccountPrivacyCard />
            <div className="bg-white dark:bg-neutral-800 border-2 border-[#0F172A] dark:border-neutral-700 rounded-xl p-6 shadow-[4px_4px_0px_#0F172A]">
              <PushNotificationSettings />
            </div>
            <TelegramLinkCard />
          </div>
        </div>
      )}

      {/* Contractor Profile Preview */}
      {profileContractorId && (
        <ContractorProfileModal
          contractorId={profileContractorId}
          onClose={() => setProfileContractorId(null)}
          onMessage={(id, name, image) => {
            setProfileContractorId(null);
            openChatWith(id, name, image);
          }}
          onReview={(() => {
            const c = reviewableContractors.find(rc => rc.contractor_id === profileContractorId && !rc.already_reviewed);
            return c ? () => { setProfileContractorId(null); setReviewTarget(c); } : null;
          })()}
          homeownerLeads={leads}
          onQuoteRequested={() => fetchQuoteRequests()}
        />
      )}

      {/* Review Modal */}
      {reviewTarget && (
        <ReviewModal
          contractor={reviewTarget}
          onClose={() => setReviewTarget(null)}
          onSubmitted={fetchReviewableContractors}
          currentUserId={currentUserId}
        />
      )}

      {/* Messaging Sidebar */}
      <MessagingSidebar
        isOpen={messagingOpen}
        onClose={() => { setMessagingOpen(false); setActiveChatPartner(null); }}
        activeChatPartner={activeChatPartner}
        onOpenChat={openChatWith}
        currentUserId={currentUserId}
      />

      {/* CSS for slide-in animation */}
      <style>{`
        @keyframes slideIn { from { transform: translateX(100%); } to { transform: translateX(0); } }
        .animate-slide-in { animation: slideIn 0.25s ease-out; }
      `}</style>

      <MembersFooter role="homeowner" />
    </div>
    </PullToRefresh>
  );
}
